import { FileSystemDirectoryHandle } from "file-system-access";
import { stringify } from "smol-toml";
import { Library, LibraryMap, isLibraryKey } from "../types/library.type";
import { MANIFEST_FILE_NAME, PythonManifest } from "../types/manifest.type";
import { RequirementsTxt } from "./requirements/requirementsTxt";

export async function bootstrapPythonProject(
  m: PythonManifest,
  dirHandle: FileSystemDirectoryHandle
) {
  if (!isLibraryKey(m.python.library)) throw new Error("Library not selected");

  const library = LibraryMap[m.python.library];
  let writers: FileSystemWritableFileStream[] = [];

  try {
    const [manifestHandle, mw] = await createManifestToml(m, dirHandle);
    if (mw) writers.push(mw);

    const [appHandle, aw] = await bootstrapApp(m, library, dirHandle);
    if (aw) writers.push(aw);

    const [requirementsHandle, rw] = await bootstrapRequirements(
      m,
      library,
      dirHandle
    );
    if (rw) writers.push(rw);

    const [coverImageHandle, pw] = await bootstrapAppCoverImage(m, dirHandle);
    if (pw) writers.push(pw);

    await Promise.all(writers.map((w) => w.close()));

    return {
      manifestHandle,
      appHandle,
      requirementsHandle,
      coverImageHandle
    };
  } catch (error) {
    // If error occurs, abort all writing
    await Promise.all(writers.map((w) => !w.locked && w.abort()));

    throw error;
  }
}

async function createManifestToml(
  m: PythonManifest,
  dirHandle: FileSystemDirectoryHandle
): Promise<[FileSystemFileHandle, FileSystemWritableFileStream]> {
  const manifestHandle = await dirHandle.getFileHandle(MANIFEST_FILE_NAME, {
    create: true
  });

  const manifestToml = stringify(m);
  const writer = await manifestHandle.createWritable();
  await writer.write(manifestToml);

  return [manifestHandle, writer];
}

async function bootstrapApp(
  m: PythonManifest,
  library: Library,
  dirHandle: FileSystemDirectoryHandle
): Promise<[FileSystemFileHandle, FileSystemWritableFileStream | null]> {
  // touch app file and create with template if it doesn't exist
  const appHandle = await dirHandle.getFileHandle(m.python.app_file, {
    create: true
  });

  if ((await appHandle.getFile()).size === 0) {
    const writer = await appHandle.createWritable();
    await writer.write(library.defaultApp);
    return [appHandle, writer];
  }

  return [appHandle, null];
}

async function bootstrapRequirements(
  m: PythonManifest,
  library: Library,
  dirHandle: FileSystemDirectoryHandle
): Promise<[FileSystemFileHandle, FileSystemWritableFileStream]> {
  const requirementsHandle = await dirHandle.getFileHandle(
    m.python.requirements_file,
    { create: true }
  );
  const requirementsFile = await requirementsHandle.getFile();
  const arrayBuffer = await requirementsFile.arrayBuffer();
  const reqTxt = await RequirementsTxt.read(new Uint8Array(arrayBuffer));

  reqTxt.add(...library.requirements);
  const writer = await requirementsHandle.createWritable();
  await reqTxt.write(writer);

  return [requirementsHandle, writer];
}

async function bootstrapAppCoverImage(
  m: PythonManifest,
  dirHandle: FileSystemDirectoryHandle
): Promise<[FileSystemFileHandle, FileSystemWritableFileStream | null]> {
  const coverImageHandle = await dirHandle.getFileHandle(m.cover_image, {
    create: true
  });

  if ((await coverImageHandle.getFile()).size > 0)
    return [coverImageHandle, null];

  // If the file is empty, copy the placeholder image
  const placeholderImagePath = "/app-placeholder-picture.png";
  const placeholderImageResponse = await fetch(placeholderImagePath);

  if (!placeholderImageResponse.ok) {
    throw new Error(
      `Failed to fetch placeholder image: ${placeholderImageResponse.statusText}`
    );
  }

  const placeholderImageBlob = await placeholderImageResponse.blob();
  const writer = await coverImageHandle.createWritable();
  await writer.write(placeholderImageBlob);

  return [coverImageHandle, writer];
}
